import { useMemo } from 'react';

import { useGetOptionsForCLA } from 'hooks/useGetOptionsForCLA';
import { useTranslation } from 'react-i18next';

import { SalaryScalesFlag } from '../types';

import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';
import { numberFormatter } from 'components/shared/DataGrid/utils/valueFormatters/numberFormatter';
import { singleSelectValueFormatter } from 'components/shared/DataGrid/utils/valueFormatters/singleSelectValueFormatter';
import { booleanValueGetter } from 'components/shared/DataGrid/utils/valueGetters/booleanValueGetter';
import { nestedFieldValueGetter } from 'components/shared/DataGrid/utils/valueGetters/nestedFieldValueGetter';

import { GridColDef } from '@mui/x-data-grid-pro';
import BooleanCell from 'components/shared/Table/TableCell/DataGrid/BooleanCell';

export const useGetColumnDefs = (): GridColDef[] => {
  const { t } = useTranslation('common');

  const options = useGetOptionsForCLA();

  return useMemo(
    () => [
      {
        field: 'claId',
        type: 'singleSelect',
        headerName: t('salaryScales.grid.claId'),
        hideable: false,
        valueOptions: options,
        valueFormatter: (params) => singleSelectValueFormatter(params, options),
        minWidth: 50,
        flex: 1
      },
      {
        field: 'scale',
        type: 'string',
        headerName: t('salaryScales.grid.scale'),
        minWidth: 50
      },
      {
        field: 'seniority',
        type: 'number',
        headerName: t('salaryScales.grid.seniority'),
        width: 100,
        flex: 1,
        headerAlign: 'left',
        align: 'left'
      },
      {
        field: 'salaryNr',
        type: 'number',
        headerName: t('salaryScales.grid.salaryNr'),
        minWidth: 100,
        flex: 1,
        headerAlign: 'left',
        align: 'left'
      },
      {
        field: 'periodSalary',
        type: 'number',
        headerName: t('salaryScales.grid.periodSalary'),
        valueFormatter: numberFormatter,
        minWidth: 120,
        flex: 1,
        headerAlign: 'left',
        align: 'left'
      },
      {
        field: SalaryScalesFlag.BUH_BLOCKED as SalaryScalesFlag,
        type: 'boolean',
        headerName: t('salaryScales.grid.flags.BUH_BLOCKED'),
        valueGetter: booleanValueGetter,
        renderCell: ({ value }) => <BooleanCell value={value} />,
        minWidth: 80
      },
      {
        field: SalaryScalesFlag.BONUS_BLOCKED as SalaryScalesFlag,
        type: 'boolean',
        headerName: t('salaryScales.grid.flags.BONUS_BLOCKED'),
        valueGetter: booleanValueGetter,
        renderCell: ({ value }) => <BooleanCell value={value} />,
        minWidth: 80
      },
      {
        field: 'scaleExt',
        type: 'string',
        headerName: t('salaryScales.grid.scaleExt'),
        minWidth: 100,
        flex: 1
      },
      {
        field: 'seniorityExt',
        type: 'string',
        headerName: t('salaryScales.grid.seniorityExt'),
        width: 100,
        flex: 1
      },
      {
        field: 'claExt',
        type: 'string',
        headerName: t('salaryScales.grid.claExt'),
        minWidth: 100,
        flex: 1
      },
      {
        field: 'valid.from',
        type: 'date',
        valueGetter: nestedFieldValueGetter,
        valueFormatter: dateFormatter,
        headerName: t('validFrom'),
        minWidth: 110
      },
      {
        field: 'valid.to',
        type: 'date',
        valueGetter: nestedFieldValueGetter,
        valueFormatter: dateFormatter,
        headerName: t('validTo'),
        minWidth: 110
      }
    ],
    [t, options]
  );
};
